import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { QUOTE_SUB_ITEM_TYPE_CUSTOM_LINE } from './consts'

/** Main line only (excludes nested quote_sub_item amounts). */
export const calculateLineItemMainOnly = (item: QuoteItem_): number =>
  Math.max(
    0,
    (item.unit_price || 0) * (item.quantity || 0) - (item.discount || 0)
  )

const calculateSubItemAmount = (sub: {
  unit_price?: number | null
  quantity?: number | null
}): number => (sub.unit_price || 0) * (sub.quantity || 0)

export const calculateLineItemTotal = (item: QuoteItem_): number => {
  const main = calculateLineItemMainOnly(item)
  const subs =
    item.quote_sub_item?.reduce(
      (acc, sub) => acc + calculateSubItemAmount(sub),
      0
    ) ?? 0
  return main + subs
}

const bucketForQuoteItemType = (
  type: string | null | undefined
): 'product' | 'shipping' | 'custom' => {
  if (type === 'product' || type === 'shipping' || type === 'custom') {
    return type
  }
  return 'custom'
}

export type QuoteFooterSubtotals = {
  productBase: number
  decoration: number
  setupCost: number
  productCustomLines: number
  shipping: number
  customQuoteItems: number
  otherSubItems: number
}

/**
 * Breakdown for the order footer: main lines by quote_item.type, plus quote_sub_item
 * sums by decoration / setup / custom_line / other. Sums to calculateQuoteTotal.
 */
export const calculateQuoteFooterSubtotals = (
  quoteItems: QuoteItem_[] | undefined
): QuoteFooterSubtotals => {
  const subtotals: QuoteFooterSubtotals = {
    productBase: 0,
    decoration: 0,
    setupCost: 0,
    productCustomLines: 0,
    shipping: 0,
    customQuoteItems: 0,
    otherSubItems: 0,
  }

  quoteItems?.forEach((item) => {
    const main = calculateLineItemMainOnly(item)
    const bucket = bucketForQuoteItemType(item.type)
    if (bucket === 'product') subtotals.productBase += main
    else if (bucket === 'shipping') subtotals.shipping += main
    else subtotals.customQuoteItems += main

    item.quote_sub_item?.forEach((sub) => {
      const amt = calculateSubItemAmount(sub)
      switch (sub.type) {
        case 'decoration':
          subtotals.decoration += amt
          break
        case 'setup_cost':
          subtotals.setupCost += amt
          break
        case QUOTE_SUB_ITEM_TYPE_CUSTOM_LINE:
          subtotals.productCustomLines += amt
          break
        default:
          subtotals.otherSubItems += amt
      }
    })
  })

  return subtotals
}

export const calculateQuoteTotal = (
  quoteItems: QuoteItem_[] | undefined
): number =>
  quoteItems?.reduce((acc, item) => acc + calculateLineItemTotal(item), 0) ?? 0

export type ProductReceiptLineKind =
  | 'product'
  | 'decoration'
  | 'setup_cost'
  | 'custom_line'
  | 'other'

export type ProductReceiptLine = {
  key: string
  kind: ProductReceiptLineKind
  subItemId?: number
  label: string
  unitPrice: number
  quantity: number
  /** When true, show unit × qty (product / decoration). Setup/custom often hide it. */
  showDetail: boolean
  amount: number
  /** Product label is a fixed translation, not a DB field. */
  labelEditable: boolean
}

const receiptKindForSubType = (
  type: string | null | undefined
): ProductReceiptLineKind => {
  if (type === 'decoration' || type === 'setup_cost') return type
  if (type === QUOTE_SUB_ITEM_TYPE_CUSTOM_LINE) return 'custom_line'
  return 'other'
}

/** Flat receipt lines for one product — shown inline instead of nested table rows. */
export const getProductReceiptLines = (
  item: QuoteItem_,
  t: (en?: string, sv?: string) => string,
  overrides?: {
    unitPrice?: number
    quantity?: number
    discount?: number
    /** Override the main line label (e.g. custom quote items). */
    mainLabel?: string
  }
): ProductReceiptLine[] => {
  const unitPrice = overrides?.unitPrice ?? item.unit_price ?? 0
  const quantity = overrides?.quantity ?? item.quantity ?? 0
  const discount = overrides?.discount ?? item.discount ?? 0
  const productBase = Math.max(0, unitPrice * quantity - discount)

  const lines: ProductReceiptLine[] = [
    {
      key: 'product',
      kind: 'product',
      label: overrides?.mainLabel ?? t('Product', 'Produkt'),
      unitPrice,
      quantity,
      showDetail: true,
      amount: productBase,
      labelEditable: false,
    },
  ]

  item.quote_sub_item?.forEach((sub) => {
    const subUnitPrice = sub.unit_price ?? 0
    const subQuantity = sub.quantity ?? 0
    const amount = calculateSubItemAmount(sub)
    if (amount <= 0 && !sub.description) return

    const kind = receiptKindForSubType(sub.type)
    const showDetail = kind === 'decoration' || kind === 'other'

    if (kind === 'setup_cost') {
      lines.push({
        key: `sub-${sub.id}`,
        kind,
        subItemId: sub.id,
        label: sub.description || t('Setup', 'Start'),
        unitPrice: subUnitPrice,
        quantity: subQuantity || 1,
        showDetail: false,
        amount,
        labelEditable: true,
      })
      return
    }

    if (kind === 'custom_line') {
      lines.push({
        key: `sub-${sub.id}`,
        kind,
        subItemId: sub.id,
        label: sub.description || t('Extra line', 'Extrarad'),
        unitPrice: subUnitPrice,
        quantity: subQuantity || 1,
        showDetail: false,
        amount,
        labelEditable: true,
      })
      return
    }

    lines.push({
      key: `sub-${sub.id}`,
      kind,
      subItemId: sub.id,
      label: sub.description || t('Decoration', 'Dekoration'),
      unitPrice: subUnitPrice,
      quantity: subQuantity,
      showDetail: true,
      amount,
      labelEditable: true,
    })
  })

  return lines
}

export type ProductRowBreakdown = {
  productBase: number
  decoration: number
  setupCost: number
  customLines: number
  other: number
  total: number
}

/** Per-product price stack for inline breakdown UI. */
export const calculateProductRowBreakdown = (
  item: QuoteItem_
): ProductRowBreakdown => {
  const breakdown: ProductRowBreakdown = {
    productBase: calculateLineItemMainOnly(item),
    decoration: 0,
    setupCost: 0,
    customLines: 0,
    other: 0,
    total: 0,
  }

  item.quote_sub_item?.forEach((sub) => {
    const amt = calculateSubItemAmount(sub)
    switch (sub.type) {
      case 'decoration':
        breakdown.decoration += amt
        break
      case 'setup_cost':
        breakdown.setupCost += amt
        break
      case QUOTE_SUB_ITEM_TYPE_CUSTOM_LINE:
        breakdown.customLines += amt
        break
      default:
        breakdown.other += amt
    }
  })

  breakdown.total =
    breakdown.productBase +
    breakdown.decoration +
    breakdown.setupCost +
    breakdown.customLines +
    breakdown.other

  return breakdown
}
